"use client";

import {JSX, useEffect, useState} from "react";

import SmartImage from "@/shared/ui/smart-image";

import {AdvancedMarker, APIProvider, Map, useMap} from "@vis.gl/react-google-maps";

import {useTranslation} from "@/shared/i18n";
import {assetPath} from "@/shared/utils/asset-path";
import {useDevice} from "@/shared/utils/device/device-context";
import {getGoogleMapsApiKey} from "@/shared/utils/env";

import {LocationMapLayout, LocationMapLayoutProps} from "./location-map.layout";

export interface LocationMapProps extends Omit<LocationMapLayoutProps, "children"> {
    latitude: number;
    longitude: number;
    zoomControl?: boolean;
    streetViewControl?: boolean;
    mapTypeControl?: boolean;
    fullscreenControl?: boolean;
    rotateControl?: boolean;
    clickableIcons?: boolean;
    gestureHandling?: "cooperative" | "greedy" | "none" | "auto";
    zoom?: number;
    defaultZoom?: number;
    disableMarkerClick?: boolean;
}

function CustomStreetViewControl({latitude, longitude}: {latitude: number; longitude: number}): JSX.Element | null {
    const map = useMap();

    useEffect(() => {
        if (!map) return;

        const controlButton = document.createElement("button");
        controlButton.className = "custom-street-view-control";
        controlButton.type = "button";
        controlButton.title = "Open Street View";
        controlButton.style.marginLeft = "12px";

        const img = document.createElement("img");
        img.src = assetPath("/images/icons/street-view-satellite.png");
        img.alt = "Street View";
        controlButton.appendChild(img);

        controlButton.addEventListener("click", () => {
            const streetView = map.getStreetView();
            if (streetView) {
                streetView.setPosition({lat: latitude, lng: longitude});
                streetView.setVisible(true);
            }
        });

        map.controls[google.maps.ControlPosition.LEFT_BOTTOM].push(controlButton);

        return () => {
            const index = map.controls[google.maps.ControlPosition.LEFT_BOTTOM].getArray().indexOf(controlButton);
            if (index > -1) {
                map.controls[google.maps.ControlPosition.LEFT_BOTTOM].removeAt(index);
            }
        };
    }, [map, latitude, longitude]);

    return null;
}

function CustomFullscreenControl({
    text,
    latitude,
    longitude,
    isMobile,
}: {
    text: string;
    latitude: number;
    longitude: number;
    isMobile: boolean;
}): JSX.Element | null {
    const map = useMap();

    useEffect(() => {
        if (!map) return;

        const controlButton = document.createElement("button");
        controlButton.className = "custom-fullscreen-control";
        controlButton.type = "button";
        controlButton.title = text;
        controlButton.style.margin = "10px";
        controlButton.textContent = text;

        controlButton.addEventListener("click", () => {
            if (isMobile) {
                const url = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
                window.open(url, "_blank");
            } else {
                const mapContainer = map.getDiv().parentElement;
                if (!mapContainer) return;

                if (!document.fullscreenElement) {
                    void mapContainer.requestFullscreen().catch((err) => {
                        console.error("Error attempting to enable fullscreen:", err);
                    });
                } else {
                    void document.exitFullscreen();
                }
            }
        });

        map.controls[google.maps.ControlPosition.LEFT_TOP].push(controlButton);

        return () => {
            const index = map.controls[google.maps.ControlPosition.LEFT_TOP].getArray().indexOf(controlButton);
            if (index > -1) {
                map.controls[google.maps.ControlPosition.LEFT_TOP].removeAt(index);
            }
        };
    }, [map, text, latitude, longitude, isMobile]);

    return null;
}

export function LocationMap({
    title,
    location,
    note,
    className,
    showBorder,
    borderRadius,
    latitude,
    longitude,
    zoomControl = false,
    streetViewControl = false,
    mapTypeControl = false,
    fullscreenControl = false,
    rotateControl = false,
    clickableIcons = true,
    gestureHandling = "none",
    zoom,
    defaultZoom = 15,
    disableMarkerClick = false,
}: LocationMapProps): JSX.Element | null {
    const {t} = useTranslation("rental");
    const device = useDevice();
    const [isMapLoaded, setIsMapLoaded] = useState(false);

    if (!latitude || !longitude) {
        return null;
    }

    const center = {lat: latitude, lng: longitude};

    return (
        <LocationMapLayout title={title} location={location} note={note} className={className} showBorder={showBorder} borderRadius={borderRadius}>
            {!isMapLoaded && <div className="location-map__skeleton" />}
            <APIProvider apiKey={getGoogleMapsApiKey()} language="en" region="US">
                <Map
                    defaultCenter={center}
                    zoom={zoom}
                    defaultZoom={defaultZoom}
                    gestureHandling={gestureHandling}
                    disableDefaultUI={true}
                    zoomControl={zoomControl}
                    zoomControlOptions={{position: 9}}
                    streetViewControl={false}
                    mapTypeControl={mapTypeControl}
                    rotateControl={rotateControl}
                    clickableIcons={clickableIcons}
                    scrollwheel={false}
                    mapId="location-map"
                    style={{height: "350px", width: "100%", opacity: isMapLoaded ? 1 : 0, transition: "opacity 0.3s ease"}}
                    onTilesLoaded={() => setIsMapLoaded(true)}
                >
                    <AdvancedMarker position={center} clickable={!disableMarkerClick}>
                        <SmartImage src={assetPath("/images/icons/marker-icon.svg")} alt={t("locationMarker")} width={33} height={44} />
                    </AdvancedMarker>
                    {fullscreenControl && (
                        <CustomFullscreenControl text={t("viewLargerMap")} latitude={latitude} longitude={longitude} isMobile={device.isMobile} />
                    )}
                    {streetViewControl && <CustomStreetViewControl latitude={latitude} longitude={longitude} />}
                </Map>
            </APIProvider>
        </LocationMapLayout>
    );
}
